import {call, put, select, takeLatest} from "redux-saga/effects";
import {
    awaiting,
    completed,
    IGetStoresFromSearch,
    IGetStoresInMapAction,
    noStoresFound,
    setStores,
    setUserLocation,
    StoresAction
} from "./stores.actions";
import {IStoreState} from "@montana/store/store";
import {getStores, getStoresFromTextSearch, getStoresInMapBounds} from "@montana/services/store";
import {IEnhancedStore, OmitConfig} from "@montana/models/dev";
import {IStoresContainerState} from "./stores";
import {createLocation} from "@montana/pages/store-locator-page/store-locator.service";
import {getUserLocation} from "@montana/helpers/user-location.helper";
import {isBrowser} from "@montana/helpers/browser.helper";
import {ILocation} from "@montana/shared/map";
import {computeDistance} from "@montana/helpers/distance-calculation.helper";
import {connectServiceFunctions, selectCurrentUrlLocale} from "../base/base.sagas";
import {IMarket} from '@montana/models/generated/cms.interface';
import {ISettingsContainerState} from '@montana/containers/settings/settings';

export function* onGetStores(markets: IMarket[]) {
    try {
        const market = markets.find(market => market.isSelected);
        const language: string = yield selectCurrentUrlLocale(market?.language);
        const [cGetStores]: [OmitConfig<typeof getStores>] = yield connectServiceFunctions(getStores);
        const response: Response = yield call(cGetStores, language, market?.marketId ?? '');

        const { ok, json } = response;

        if (!ok) {
            throw new Error();
        } else {
            const storesData: IEnhancedStore[] = yield call(json.bind(response));

            const transformedStoresData: IEnhancedStore[] = storesData.map(store => ({
                ...store,
                location: createLocation(store) || undefined,
                selectedProduct: store.montanaFree || store.montanaMini || store.pantonWire
            }));

            return yield put(setStores(transformedStoresData));
        }
    } catch(e) {
        yield put(completed());
        console.error('Failed to load stores');
    }
}

function* getStoresInMap(action: IGetStoresInMapAction) {
    const settings: ISettingsContainerState = yield select((store: IStoreState) => ({ ...store.settings }));
    const language: string = yield selectCurrentUrlLocale(settings.market?.language);
    const [cGetStoresInMap]: [OmitConfig<typeof getStoresInMapBounds>] = yield connectServiceFunctions(getStoresInMapBounds);
    const response: Response = yield call(cGetStoresInMap, action.worldCorners, language);

    const { ok, json } = response;

    if (!ok) {
        throw new Error();
    }
    const storesData: IEnhancedStore[] = yield call(json.bind(response));

    const transformedStoresData: IEnhancedStore[] = storesData.map(store => ({
        ...store,
        location: createLocation(store) || undefined,
        selectedProduct: store.montanaFree || store.montanaMini || store.pantonWire
    }));

    return transformedStoresData;

}

function* onGetStoresInMap(action: IGetStoresInMapAction) {
    try {
        const storesInMap = yield getStoresInMap(action);

        if (!storesInMap.length) return false;

        yield put(setStores(storesInMap));
    } catch(e) {
        yield put(completed());
        console.error('Failed to load stores');
    }
}

function* getStoresFromSearch(action: IGetStoresFromSearch) {
    const language: string = yield selectCurrentUrlLocale(action.language);
    const [cGetStoresFromSearch]: [OmitConfig<typeof getStoresFromTextSearch>] = yield connectServiceFunctions(getStoresFromTextSearch);
    const query = encodeURI(action.query);
    const response: Response = yield call(cGetStoresFromSearch, query, language);

    const {ok, json} = response;

    if (!ok) {
        throw new Error();
    }

    const storesFromSearch: IEnhancedStore[] = yield call(json.bind(response));
    const filteredStoresFromSearch = storesFromSearch.filter(store => store.name?.toLowerCase().includes(action.query) || store.city?.toLowerCase().includes(action.query) || store.country?.toLowerCase().includes(action.query));
    const transformedStoresData: IEnhancedStore[] = filteredStoresFromSearch.map(store => ({
        ...store,
        location: createLocation(store) || undefined,
        selectedProduct: store.montanaFree || store.montanaMini || store.pantonWire
    }));

    return transformedStoresData;
}

function* onGetStoresFromSearch(action: IGetStoresFromSearch) {
    try {
        const storesFromSearch = yield getStoresFromSearch(action);

        if (!storesFromSearch.length) {
            yield put(noStoresFound(true));
            return false;
        }

        yield put(setStores(storesFromSearch));
        yield put(noStoresFound(false));

    } catch (e) {
        yield put(completed());
        console.error('Failed to find any stores in search')
    }
}

function* onGetUserLocation() {
    try {
        if (isBrowser) {
            const { stores }: IStoresContainerState = yield select((s: IStoreState) => s.stores);

            const userLocation: ILocation = yield call(getUserLocation);

            // Set user location in the stores state
            if (userLocation) {
                yield put(setUserLocation(userLocation));
            }

            if (stores && userLocation) {
                yield put(awaiting());
                const updatedStores: IEnhancedStore[] = stores.map(store => {
                    return ({
                        ...store,
                        distance: !!userLocation && !!store.location ? computeDistance(userLocation, store.location) : undefined,
                    });
                }).sort((a, b) => {
                    if (!a.distance) {
                        return 1;
                    } else if (!b.distance) {
                        return -1
                    } else {
                        return a.distance - b.distance;
                    }
                });

                yield put(setStores(updatedStores));
            }
        }
    } catch(e) {
        yield put(completed());
        console.error('Failed to get user location');
    }
}

export const storesSagas = [
    //takeLatest(StoresAction.GET_STORES, onGetStores),
    takeLatest(StoresAction.GET_STORES_IN_MAP, onGetStoresInMap),
    takeLatest(StoresAction.GET_STORES_FROM_SEARCH, onGetStoresFromSearch),
    takeLatest(StoresAction.GET_USER_LOCATION, onGetUserLocation),
];
